/**
 * Generic CRUD Service
 * Provides reusable CRUD operations for all API endpoints with type safety,
 * error handling, caching, and standardized response handling.
 *
 * Enhanced with template-based URL building for nested routes and multiple endpoints.
 *
 * @module services/crud.service
 *
 * NOTE: Types have been migrated to the centralized types directory.
 * Import types from '../types/crud' or '../types' instead of this file.
 *
 * @see {@link module:types/crud} for type definitions
 */

import { apiClient } from './api/client';
import { registerSessionScoped } from '../store/session-scope';
import {
  syncGetAccountUsername,
  syncGetMeasurementSystem,
} from '../store/store-sync';
import { isPaginatedResponse } from '../types/common/pagination.types';
import { dayjs, formatLaravelDate } from '../utils/date';

import type { PaginatedResponse, ID, SearchRequest } from '../types';
import type { QueryParams } from '../types/api/query.types';
import type {
  CrudServiceConfig,
  CreateOptions,
  UpdateOptions,
  DeleteOptions,
  BulkOperationResult,
  UrlContext,
  ResolvedContext,
} from '../types/crud/crud.types';
import type { AxiosInstance } from 'axios';

// Internal type aliases for use within this file
type CrudServiceConfigInternal = CrudServiceConfig;
type QueryParamsInternal = QueryParams;
type CreateOptionsInternal = CreateOptions;
type UpdateOptionsInternal = UpdateOptions;
type DeleteOptionsInternal = DeleteOptions;
type BulkOperationResultInternal<T = unknown> = BulkOperationResult<T>;
type UrlContextInternal = UrlContext;
type ResolvedContextInternal = ResolvedContext;

/**
 * Deterministic, cross-platform short tag for a string (used as a cache-key
 * prefix). Replaces `Buffer.from(...).toString('base64')`, which relies on the
 * Node-only `Buffer` global and throws on React Native (Hermes) and in browsers
 * without a polyfill. djb2 → base36, truncated to 8 chars.
 */
function shortHashTag(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33 + input.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36).slice(0, 8);
}

// Extended API client interface with specialized methods
interface ExtendedApiClient extends AxiosInstance {
  getPaginated: (
    url: string,
    params?: QueryParamsInternal
  ) => Promise<PaginatedResponse<unknown>>;
  uploadFile: (
    url: string,
    file: File,
    onUploadProgress?: (progress: number) => void
  ) => Promise<unknown>;
}

// =============================================================================
// CACHE MANAGEMENT
// =============================================================================

class CrudCache {
  private cache = new Map<
    string,
    { data: unknown; timestamp: number; ttl: number }
  >();
  private enabled: boolean;

  constructor(enabled: boolean = false) {
    this.enabled = enabled;
  }

  public get<T>(key: string): T | null {
    if (!this.enabled) return null;

    const cached = this.cache.get(key);
    if (!cached) return null;

    // Check if cache entry has expired
    if (dayjs().valueOf() - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  public set<T>(key: string, data: T, ttl: number): void {
    if (!this.enabled) return;

    this.cache.set(key, {
      data,
      timestamp: dayjs().valueOf(),
      ttl,
    });
  }

  public delete(key: string): void {
    this.cache.delete(key);
  }

  public clear(): void {
    this.cache.clear();
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clear();
    }
  }

  public getSize(): number {
    return this.cache.size;
  }

  public getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public cleanup(): void {
    const now = dayjs().valueOf();
    for (const [key, cached] of this.cache.entries()) {
      if (now - cached.timestamp > cached.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

// =============================================================================
// GENERIC CRUD SERVICE CLASS
// =============================================================================

/**
 * The API answers every write with the resource inside a `{ data }` envelope.
 * `create`, `update`, `restore` and `archive` pass their response through this
 * so a caller gets the resource itself, as `getById` and `getAll` already give
 * it. A bare resource is left alone — some MSW fixtures still answer without
 * the envelope. A body with its own `id` is a resource, never
 * an envelope, even if it has a `data` field.
 */
export function unwrapResource<T>(body: T | { data: T }): T {
  if (
    body !== null &&
    typeof body === 'object' &&
    !('id' in body) &&
    'data' in body &&
    body.data !== null &&
    typeof body.data === 'object' &&
    !Array.isArray(body.data)
  ) {
    return body.data;
  }
  return body as T;
}

export class CrudService<
  TDetail = Record<string, unknown>,
  TList = TDetail,
  TRequest = TDetail,
> {
  protected baseUrl: string;
  protected cache: CrudCache;
  protected cacheTTL: number;
  private cacheVariesByUnit: boolean;
  private currentContext: UrlContextInternal | null = null;

  /**
   * Every caching instance, so the session boundary can empty all of them at
   * once. Instances live for the app's life (module singletons, and the
   * `withContext` derivations share their parent's cache through the
   * prototype), so nothing is ever removed.
   */
  private static readonly cachingInstances = new Set<CrudService<unknown>>();

  constructor(config: CrudServiceConfigInternal) {
    this.baseUrl = config.baseUrl;
    this.cacheTTL = config.cacheTTL || 5 * 60 * 1000; // 5 minutes default
    this.cacheVariesByUnit = config.cacheVariesByUnit ?? false;
    this.cache = new CrudCache(config.enableCaching);

    // Cleanup cache every 10 minutes
    if (config.enableCaching) {
      CrudService.cachingInstances.add(this as CrudService<unknown>);

      const cacheCleanupTimer = setInterval(
        () => this.cache.cleanup(),
        10 * 60 * 1000
      );
      // Don't let the cleanup timer keep a Node/test event loop alive (no-op on RN/browser).
      (cacheCleanupTimer as unknown as { unref?: () => void }).unref?.();
    }
  }

  /**
   * Empty every caching instance's cache. What the session boundary calls:
   * the keys already keep one account from reading another's entries, this
   * is so nothing of the old account stays in memory.
   */
  public static clearAllCaches(): void {
    for (const instance of CrudService.cachingInstances) {
      instance.clearCache();
    }
  }

  /** How many instances cache. For the guard-rail test. */
  public static countCachingInstances(): number {
    return CrudService.cachingInstances.size;
  }

  // ---------------------------------------------------------------------------
  // CONTEXT MANAGEMENT
  // ---------------------------------------------------------------------------

  /**
   * Create a new service instance with the specified URL context
   * Supports template-based URLs with parameter substitution
   *
   * Returns `this` rather than `CrudService`: `Object.create(this)` keeps the
   * caller's own prototype, so a subclass gets its own type back and can reach
   * its protected members (notably `invalidateCache`) on the result.
   */
  public withContext(context: UrlContextInternal): this {
    const contextualService = Object.create(this) as this;
    contextualService.currentContext = context;
    return contextualService;
  }

  /**
   * Resolve URL template with parameters
   */
  private resolveUrlTemplate(
    template: string,
    parameters: Record<string, string | number> = {}
  ): string {
    let resolvedUrl = template;

    // Replace template parameters like {userId} with actual values
    for (const [key, value] of Object.entries(parameters)) {
      const placeholder = `{${key}}`;
      resolvedUrl = resolvedUrl.replace(
        new RegExp(placeholder, 'g'),
        String(value)
      );
    }

    // Check for unresolved parameters
    const unresolvedParams = resolvedUrl.match(/\{[^}]+\}/g);
    if (unresolvedParams) {
      throw new Error(
        `Unresolved template parameters: ${unresolvedParams.join(', ')}`
      );
    }

    return resolvedUrl;
  }

  /**
   * Get resolved context for current operation
   */
  private getResolvedContext(): ResolvedContextInternal {
    if (this.currentContext) {
      const resolvedUrl = this.resolveUrlTemplate(
        this.currentContext.template,
        this.currentContext.parameters || {}
      );

      const cachePrefix = this.currentContext.cacheNamespace
        ? `${this.currentContext.cacheNamespace}_`
        : `ctx_${shortHashTag(this.currentContext.template)}_`;

      return {
        baseUrl: resolvedUrl,
        cachePrefix,
      };
    }

    return {
      baseUrl: this.baseUrl,
      cachePrefix: '',
    };
  }

  /**
   * Generate cache key with context awareness.
   *
   * Protected, not private: a subclass that builds its own keys (ranking)
   * must still go through here, or its entries miss the account suffix.
   */
  protected generateCacheKey(operation: string, ...parts: unknown[]): string {
    const { cachePrefix } = this.getResolvedContext();
    const keyParts = [operation, ...parts].filter(
      (part) => part !== null && part !== undefined
    );
    const baseKey = `${cachePrefix}${keyParts.join('_')}`;
    // Fold the unit in as a suffix (preserves the prefix that invalidateCache's
    // startsWith matching relies on) so unit-bearing services miss stale entries
    // after a unit switch and hit on switch-back.
    const unitKey = this.cacheVariesByUnit
      ? `${baseKey}__unit:${syncGetMeasurementSystem()}`
      : baseKey;
    // And the account, the same way and for the same reason: most of what is
    // cached is relative to whoever fetched it (their history on an exercise,
    // their view of a profile), so another account must miss. This is also
    // what keeps a response that lands after a sign-out from being served to
    // the next account — it is written under the old account's key.
    return `${unitKey}__acct:${syncGetAccountUsername() ?? '-'}`;
  }

  // ---------------------------------------------------------------------------
  // READ OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get the whole collection, never a page of it.
   *
   * Asks for everything with `paginate=false`, which the API's index endpoints
   * honour where they paginate by default. An endpoint that ignores it still
   * answers with page 1 of N, and keeping that page would silently drop the
   * rest, so a multi-page envelope throws instead. A caller that wants pages
   * uses {@link getPaginated}.
   */
  public async getAll(params?: QueryParamsInternal): Promise<TList[]> {
    const { baseUrl } = this.getResolvedContext();
    const query: QueryParamsInternal = { paginate: false, ...params };
    const cacheKey = this.generateCacheKey('getAll', JSON.stringify(query));
    const cached = this.cache.get<TList[]>(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await apiClient.get<
      TList[] | { data: TList[]; meta?: { last_page?: number } }
    >(baseUrl, { params: query });

    // The API wraps collections in a `{ data: [...] }` envelope. Unwrap it here
    // so children of CrudService always receive a bare array. Tolerate a bare
    // array too, since some MSW mock fixtures still return the unwrapped shape.
    const body = response.data;
    const lastPage = Array.isArray(body) ? undefined : body?.meta?.last_page;

    if (lastPage !== undefined && lastPage > 1) {
      throw new Error(
        `${baseUrl} answered getAll with page 1 of ${lastPage}: the endpoint ignored paginate=false, so the rest of the collection would be lost`
      );
    }

    const list = Array.isArray(body) ? body : body?.data;

    if (list) {
      this.cache.set(cacheKey, list, this.cacheTTL);
      return list;
    }

    throw new Error('Failed to fetch items');
  }

  /**
   * Get one page of the collection, with its `links`/`meta` envelope.
   *
   * Throws when the response is not a paginated envelope: a bare array, or a
   * `{ data }` with no `meta`. That is what an unpaginated endpoint returns (or
   * a list asked for everything), and it would leave callers reading an
   * undefined `meta`. A caller that wants everything uses {@link getAll}.
   */
  public async getPaginated(
    params?: QueryParamsInternal
  ): Promise<PaginatedResponse<TList>> {
    const { baseUrl } = this.getResolvedContext();
    const cacheKey = this.generateCacheKey(
      'getPaginated',
      JSON.stringify(params || {})
    );
    const cached = this.cache.get<PaginatedResponse<TList>>(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await (apiClient as ExtendedApiClient).getPaginated(
      baseUrl,
      params
    );

    if (!response) {
      throw new Error('Failed to fetch paginated data');
    }

    if (!isPaginatedResponse<TList>(response)) {
      throw new Error(
        `${baseUrl} answered getPaginated without a paginated envelope: expected { data, links, meta }`
      );
    }

    this.cache.set(cacheKey, response, this.cacheTTL);
    return response;
  }

  /**
   * Get single item by ID
   */
  public async getById(id: ID, params?: QueryParamsInternal): Promise<TDetail> {
    const { baseUrl } = this.getResolvedContext();
    const cacheKey = this.generateCacheKey(
      'getById',
      id,
      JSON.stringify(params || {})
    );
    const cached = this.cache.get<TDetail>(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await apiClient.get<{ data: TDetail }>(
      `${baseUrl}/${id}`,
      { params }
    );

    if (response.data?.data) {
      this.cache.set(cacheKey, response.data.data, this.cacheTTL);
      return response.data.data;
    }

    throw new Error(`Item with ID ${id} not found`);
  }

  /**
   * Search items with query
   */
  public async search(
    query: string,
    params?: QueryParamsInternal
  ): Promise<TList[]> {
    const { baseUrl } = this.getResolvedContext();
    const searchParams = { ...params, search: query };
    const cacheKey = this.generateCacheKey(
      'search',
      JSON.stringify(searchParams)
    );
    const cached = this.cache.get<TList[]>(cacheKey);

    if (cached) {
      return cached;
    }

    const response = await apiClient.get<TList[]>(`${baseUrl}/search`, {
      params: searchParams,
    });

    if (response.data) {
      this.cache.set(cacheKey, response.data, this.cacheTTL);
      return response.data;
    }

    return [];
  }

  /**
   * Check if item exists by ID
   */
  public async exists(id: ID): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get item count with optional filters
   */
  public async count(params?: QueryParamsInternal): Promise<number> {
    const { baseUrl } = this.getResolvedContext();
    const cacheKey = this.generateCacheKey(
      'count',
      JSON.stringify(params || {})
    );
    const cached = this.cache.get<number>(cacheKey);

    if (cached !== null && cached !== undefined) {
      return cached;
    }

    const response = await apiClient.get<{ count: number }>(
      `${baseUrl}/count`,
      { params }
    );

    if (response.data) {
      this.cache.set<number>(cacheKey, response.data.count, this.cacheTTL);
      return response.data.count;
    }

    return 0;
  }

  // ---------------------------------------------------------------------------
  // WRITE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Create new item
   */
  public async create(
    data: Partial<TRequest>,
    options: CreateOptionsInternal = {}
  ): Promise<TList> {
    const { baseUrl } = this.getResolvedContext();
    const response = await apiClient.post<TList>(baseUrl, data);

    if (response.data) {
      // Invalidate relevant cache entries
      if (!options.skipCache) {
        this.invalidateCache(['getAll', 'getPaginated', 'count']);
      }

      return unwrapResource(response.data);
    }

    throw new Error('Failed to create item');
  }

  /**
   * Update existing item
   */
  public async update(
    id: ID,
    data: Partial<TRequest>,
    options: UpdateOptionsInternal = {}
  ): Promise<TList> {
    const { baseUrl } = this.getResolvedContext();
    const method = options.partial ? 'patch' : 'put';
    const response = await apiClient[method]<TList>(`${baseUrl}/${id}`, data);

    if (response.data) {
      // Invalidate relevant cache entries
      if (!options.skipCache) {
        this.invalidateCache(
          ['getAll', 'getPaginated', 'getById', 'search'],
          id
        );
      }

      return unwrapResource(response.data);
    }

    throw new Error(`Failed to update item with ID ${id}`);
  }

  /**
   * Delete item by ID
   */
  public async delete(
    id: ID,
    options: DeleteOptionsInternal = {}
  ): Promise<void> {
    const { baseUrl } = this.getResolvedContext();
    const params = options.soft ? { soft: true } : undefined;
    const config = options.cascade ? { params: { cascade: true } } : { params };

    const response = await apiClient.delete<{ message: string }>(
      `${baseUrl}/${id}`,
      config
    );

    if (response.status === 204) {
      // Invalidate relevant cache entries
      this.invalidateCache(
        ['getAll', 'getPaginated', 'getById', 'search', 'count'],
        id
      );
    } else {
      throw new Error(`Failed to delete item with ID ${id}`);
    }
  }

  // ---------------------------------------------------------------------------
  // BULK OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Create multiple items
   */
  public async bulkCreate(
    items: Partial<TDetail>[]
  ): Promise<BulkOperationResultInternal<TDetail>> {
    const { baseUrl } = this.getResolvedContext();
    const response = await apiClient.post<BulkOperationResultInternal<TDetail>>(
      `${baseUrl}/bulk`,
      { items }
    );

    if (response.data) {
      // Invalidate relevant cache entries
      this.invalidateCache(['getAll', 'getPaginated', 'count']);
      return response.data;
    }

    throw new Error('Failed to bulk create items');
  }

  /**
   * Update multiple items
   */
  public async bulkUpdate(
    updates: Array<{ id: ID; data: Partial<TDetail> }>
  ): Promise<BulkOperationResultInternal<TDetail>> {
    const { baseUrl } = this.getResolvedContext();
    const response = await apiClient.put<BulkOperationResultInternal<TDetail>>(
      `${baseUrl}/bulk`,
      { updates }
    );

    if (response.data) {
      // Invalidate relevant cache entries
      this.invalidateCache(['getAll', 'getPaginated', 'getById', 'search']);
      return response.data;
    }

    throw new Error('Failed to bulk update items');
  }

  /**
   * Delete multiple items
   */
  public async bulkDelete(
    ids: ID[],
    options: DeleteOptionsInternal = {}
  ): Promise<BulkOperationResultInternal> {
    const { baseUrl } = this.getResolvedContext();
    const requestData = {
      ids,
      soft: options.soft,
      cascade: options.cascade,
    };

    const response = await apiClient.delete<BulkOperationResultInternal>(
      `${baseUrl}/bulk`,
      { data: requestData }
    );

    if (response.data) {
      // Invalidate relevant cache entries
      this.invalidateCache([
        'getAll',
        'getPaginated',
        'getById',
        'search',
        'count',
      ]);
      return response.data;
    }

    throw new Error('Failed to bulk delete items');
  }

  // ---------------------------------------------------------------------------
  // SPECIALIZED OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Duplicate an existing item
   */
  public async duplicate(
    id: ID,
    modifications?: Partial<TDetail>
  ): Promise<TList> {
    const originalItem = await this.getById(id);
    const duplicateData = { ...originalItem, ...modifications };

    // Remove ID and timestamps for duplication
    delete (duplicateData as Record<string, unknown>).id;
    delete (duplicateData as Record<string, unknown>).createdAt;
    delete (duplicateData as Record<string, unknown>).updatedAt;

    return this.create(duplicateData);
  }

  /**
   * Restore soft-deleted item
   */
  public async restore(id: ID): Promise<TList> {
    const { baseUrl } = this.getResolvedContext();
    const response = await apiClient.post<TList>(`${baseUrl}/${id}/restore`);

    if (response.data) {
      // Invalidate relevant cache entries
      this.invalidateCache([
        'getAll',
        'getPaginated',
        'getById',
        'search',
        'count',
      ]);
      return unwrapResource(response.data);
    }

    throw new Error(`Failed to restore item with ID ${id}`);
  }

  /**
   * Restore a soft-deleted item via the backend's shared undo-delete route.
   *
   * Unlike {@link restore}, this targets the cross-resource
   * `PATCH /undo-delete/{resource}/{id}` endpoint, so the caller supplies the
   * backend resource slug — which can differ from this service's baseUrl
   * (e.g. custom exercises use the `exercise` slug, not `custom-exercise`).
   *
   * @param resource - backend resource slug ('plan' | 'workout' | 'exercise')
   * @param id - hashid of the soft-deleted record
   */
  public async undoDelete(resource: string, id: ID): Promise<void> {
    const response = await apiClient.patch<{ message?: string }>(
      `/undo-delete/${resource}/${id}`
    );

    if (response.status >= 200 && response.status < 300) {
      // Invalidate cached lists so the restored record reappears on refetch.
      this.invalidateCache(
        ['getAll', 'getPaginated', 'getById', 'search', 'count'],
        id
      );
      return;
    }

    throw new Error(`Failed to restore ${resource} with ID ${id}`);
  }

  /**
   * Archive item
   */
  public async archive(id: ID): Promise<TList> {
    const { baseUrl } = this.getResolvedContext();
    const response = await apiClient.post<TList>(`${baseUrl}/${id}/archive`);

    if (response.data) {
      // Invalidate relevant cache entries
      this.invalidateCache(['getAll', 'getPaginated', 'getById', 'search']);
      return unwrapResource(response.data);
    }

    throw new Error(`Failed to archive item with ID ${id}`);
  }

  /**
   * Export items in various formats
   */
  public async export(
    format: 'csv' | 'json' | 'xlsx',
    params?: QueryParamsInternal
  ): Promise<Blob> {
    const { baseUrl } = this.getResolvedContext();
    const response = await apiClient.get(`${baseUrl}/export`, {
      params: { ...params, format },
      responseType: 'blob',
    });

    return response as unknown as Blob;
  }

  // ---------------------------------------------------------------------------
  // CACHE MANAGEMENT
  // ---------------------------------------------------------------------------

  /**
   * Invalidate cache entries for specific operations.
   *
   * Scope follows the calling context, because that is what determines how
   * much the caller can actually know:
   *
   * - Under a context (`withContext`), the write affects only that context's
   *   entries, so only those are cleared. A create in `user123` must not throw
   *   away `user456`'s cached list.
   * - Contextless, the write has only an id and cannot tell which namespaces
   *   hold a listing containing that record — so every namespace's copy of the
   *   operation goes. Without this, a per-user list (`{username}-goals_getAll…`)
   *   survived contextless writes like `update`, `delete`, `undoDelete` and the
   *   status transitions, whose prefixes only ever matched `getAll…`. A read
   *   inside the TTL then returned pre-write data.
   */
  protected invalidateCache(operations: string[], id?: ID): void {
    const { cachePrefix } = this.getResolvedContext();
    const keys = this.cache.getKeys();

    // No context means no way to know which namespaces are affected.
    const acrossNamespaces = cachePrefix === '';

    for (const key of keys) {
      if (!acrossNamespaces && !key.startsWith(cachePrefix)) {
        continue;
      }

      const shouldInvalidate = operations.some((op) => {
        const target = id && op === 'getById' ? `getById_${id}` : op;

        if (!acrossNamespaces) {
          return key.startsWith(`${cachePrefix}${target}`);
        }

        // The operation must sit on a `_` boundary, so `getById` cannot match
        // a namespace that merely ends with it.
        return (
          key === target ||
          key.startsWith(`${target}_`) ||
          key.includes(`_${target}_`) ||
          key.endsWith(`_${target}`)
        );
      });

      if (shouldInvalidate) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cache
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  public getCacheStats(): { size: number; enabled: boolean } {
    return {
      size: this.cache.getSize(),
      enabled: this.cache.isEnabled(),
    };
  }

  /**
   * Enable/disable caching
   */
  public setCacheEnabled(enabled: boolean): void {
    this.cache.setEnabled(enabled);
  }

  // ---------------------------------------------------------------------------
  // UTILITY METHODS
  // ---------------------------------------------------------------------------

  /**
   * Get base URL for this service
   */
  public getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Set base URL for this service
   */
  public setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
    this.clearCache(); // Clear cache when URL changes
  }

  /**
   * Validate item data before operations
   */
  protected validateData(_data: Partial<TRequest>): void {
    // Override in subclasses for specific validation
  }

  /**
   * Transform data before sending to API
   */
  protected transformRequest(data: Partial<TRequest>): Partial<TRequest> {
    // Override in subclasses for data transformation
    return data;
  }

  /**
   * Transform data received from API
   */
  protected transformResponse(data: TDetail): TDetail {
    // Override in subclasses for data transformation
    return data;
  }

  /**
   * Transform search parameters for API compatibility
   * Converts frontend search format to backend-expected format
   *
   * @param params - Search parameters from frontend
   * @returns Transformed parameters for API
   */
  protected transformSearchParams(params: SearchRequest): QueryParamsInternal {
    const transformed: QueryParamsInternal = {};

    // Map frontend parameters to API parameters. `difficulty_level` keeps its
    // name and goes lowercase: the API validates it against the enum values
    // (`easy`), not the labels the payload carries (`Easy`).
    if (params.difficulty_level)
      transformed.difficulty_level = params.difficulty_level.toLowerCase();
    if (params.shared !== undefined) transformed.shared = params.shared;
    if (params.status) transformed.status = params.status;
    if (params.gender) transformed.gender = params.gender;
    if (params.plan) transformed.plan = params.plan;
    if (params.term) transformed.search = params.term;
    // Calendar dates: sent as the local day, never converted to UTC.
    if (params.start_date)
      transformed.start_date = formatLaravelDate(params.start_date);
    if (params.end_date)
      transformed.end_date = formatLaravelDate(params.end_date);
    if (params.measurement) transformed.measurement = params.measurement;
    if (params.equipment) transformed.equipment = params.equipment;
    if (params.body_part) transformed.body_part = params.body_part;
    if (params.muscle_group) transformed.muscle_group = params.muscle_group;
    if (params.page) transformed.page = params.page;
    if (params.paginate !== undefined) transformed.paginate = params.paginate;

    return transformed;
  }

  /**
   * Transform array results to a single-page paginated response.
   * Builds the Laravel `{ data, links, meta }` shape with all results on one page.
   *
   * @param results - Array of results to paginate
   * @returns Paginated response object
   */
  protected transformToPaginated(results: TList[]): PaginatedResponse<TList> {
    const total = results.length;
    return {
      data: results,
      links: {
        first: null,
        last: null,
        prev: null,
        next: null,
      },
      meta: {
        current_page: 1,
        from: total > 0 ? 1 : null,
        last_page: 1,
        links: [],
        path: this.baseUrl,
        per_page: total,
        to: total > 0 ? total : null,
        total,
      },
    };
  }
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Create a new CRUD service instance
 */
export const createCrudService = <TDetail, TList = TDetail>(
  config: CrudServiceConfigInternal
): CrudService<TDetail, TList> => {
  return new CrudService<TDetail, TList>(config);
};

/**
 * Create multiple CRUD service instances
 */
export const createCrudServices = <
  TConfigs extends Record<string, CrudServiceConfigInternal>,
>(
  configs: TConfigs
): { [K in keyof TConfigs]: CrudService<unknown, unknown> } => {
  const services = {} as {
    [K in keyof TConfigs]: CrudService<unknown, unknown>;
  };

  for (const [key, config] of Object.entries(configs)) {
    services[key as keyof TConfigs] = new CrudService(
      config as CrudServiceConfigInternal
    );
  }

  return services;
};

// =============================================================================
// SESSION SCOPE
// =============================================================================

// One registration for every caching service, present and future: the static
// registry is filled by the constructor, so a new subclass is covered the
// moment it is instantiated.
registerSessionScoped('crudServiceCaches', () => {
  CrudService.clearAllCaches();
});
